import React, { useMemo } from "react";

import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";

import Table from "@app/component/Table";
import TextWithInfo from "@app/component/typography/TextWithInfo";
import TextWithLine from "@app/component/typography/TextWithLine";
import { ServiceDescription } from "@app/store/integrations/supported-services";
import Integration from "@app/types/integrations/Integration";
import IntegrationUser from "@app/types/integrations/IntegrationUser";

import IntegrationCard from "../IntegrationCard";

import { IntegrationUserWithSyncUnmappedUsers } from "./Step1";
import styles from "./Steps.module.scss";
import useUserTableColumnConfig, { UNASSIGNED_USER_ID } from "./useUserTableColumnConfig";

const unassignedIntegrationUser: IntegrationUserWithSyncUnmappedUsers = {
  id: UNASSIGNED_USER_ID,
  active: false,
  crmId: "",
  email: "",
  firstName: "",
  integrationId: -1,
  lastName: "",
  orgId: -1,
  syncing: undefined,
  userId: null,
};

interface Props {
  integration?: Integration;
  integrationUsers: IntegrationUser[];
  mmcUsers: User[];
  onChangeUnassignedRecordsOwner: (mmcUserId?: User["id"]) => void;
  onChangeUserMapping: (mapping: {
    integrationUser: IntegrationUser;
    mmcUserId?: User["id"];
  }) => void;
  selectedService: ServiceDescription;
}

const UserMapping: React.FC<Props> = ({
  integration,
  integrationUsers,
  mmcUsers,
  onChangeUnassignedRecordsOwner,
  onChangeUserMapping,
  selectedService,
}) => {
  const intl = useIntl();

  const userTableColumnConfig = useUserTableColumnConfig(
    intl,
    onChangeUserMapping,
    selectedService?.name ?? "",
    mmcUsers,
    onChangeUnassignedRecordsOwner,
    integration?.metadata?.unassignedRecordsOwner
  );

  const integrationUsersWithSyncUnmappedUsers: IntegrationUserWithSyncUnmappedUsers[] =
    useMemo(() => {
      const result = integrationUsers.map<IntegrationUserWithSyncUnmappedUsers>((user) => ({
        ...user,
        syncUnmappedUsers: integration?.syncUnmappedUsers,
      }));
      result.unshift(unassignedIntegrationUser);
      return result;
    }, [integrationUsers, integration]);

  return (
    <div className={styles.userContainer}>
      <TextWithLine>
        <TextWithInfo
          info={intl.formatMessage({
            id: "integrations.setupStep.selectUsers.tooltip",
            defaultMessage:
              "Match the users from your CRM to the users in Map My Customers. Each user must have a license for both your CRM and Map My Customers.",
            description: "Select users information",
          })}
        >
          {intl.formatMessage({
            id: "integrations.setupStep.selectUsers",
            defaultMessage: "Select user(s)",
            description: "Select users text",
          })}
        </TextWithInfo>
      </TextWithLine>
      <IntegrationCard>
        <div className={styles.heightLimitedContainer}>
          <Table<IntegrationUserWithSyncUnmappedUsers>
            columns={userTableColumnConfig}
            dataSource={integrationUsersWithSyncUnmappedUsers}
            noHover
            pagination={false}
            rowKey="email"
          />
        </div>
      </IntegrationCard>
    </div>
  );
};

export default UserMapping;
